
import { useEffect, useState } from 'react';
import AnimatedBackground from '../../../components/feature/AnimatedBackground';
import LanguageSelector from '../../../components/feature/LanguageSelector';
import { useTranslation } from '../../../hooks/useTranslation';

export default function HeroSection() {
  const { t } = useTranslation();
  const [terminalText, setTerminalText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    const text = t('heroTerminal');
    let index = 0;
    
    const typeWriter = setInterval(() => {
      if (index < text.length) {
        setTerminalText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typeWriter);
      }
    }, 100);

    const cursorBlink = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => {
      clearInterval(typeWriter);
      clearInterval(cursorBlink);
    };
  }, [t]);

  return (
    <section className="relative min-h-screen bg-black overflow-hidden">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* Language Selector */}
      <div className="absolute top-6 right-6 z-20">
        <LanguageSelector />
      </div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-20 flex items-center min-h-screen">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="text-cyan-400 font-mono text-sm tracking-wider">
                {terminalText}
                <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity`}>|</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-white font-mono leading-tight">
                Iker Peralta
                <span className="block text-cyan-400">Condes</span>
              </h1>
              
              <h2 className="text-xl lg:text-2xl text-gray-300 font-mono">
                {t('heroSubtitle')}
              </h2>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-gray-400 font-mono">
                <i className="ri-shield-check-line text-cyan-400"></i>
                <span>{t('threatDetection')}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 font-mono">
                <i className="ri-search-eye-line text-cyan-400"></i>
                <span>{t('darkWebIntel')}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400 font-mono">
                <i className="ri-bug-line text-cyan-400"></i>
                <span>{t('threatHunting')}</span>
              </div>
            </div>

            <div className="flex space-x-4">
              <a 
                href="#contact" 
                className="bg-cyan-500 hover:bg-cyan-400 text-black px-8 py-3 font-mono font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25 whitespace-nowrap cursor-pointer"
              >
                {t('contactMe')}
              </a>
              <a 
                href="#about" 
                className="border border-cyan-500 text-cyan-400 hover:bg-cyan-500 hover:text-black px-8 py-3 font-mono font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {t('learnMore')}
              </a>
            </div>
          </div>

          {/* Right Content - Profile Image */}
          <div className="flex justify-center lg:justify-end">
            <div className="relative">
              {/* Glowing border effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg blur-sm opacity-75 animate-pulse"></div>
              
              {/* Profile image container */}
              <div className="relative bg-gray-900 p-1 rounded-lg">
                <img 
                  src="https://static.readdy.ai/image/1ab09db729383207b13912428634dabd/a14acbedaa5f5149229d4329bea37839.png"
                  alt="Iker Peralta Condes - Cyber Threat Intelligence Analyst"
                  className="w-80 h-80 lg:w-96 lg:h-96 object-cover rounded-lg"
                />
              </div>
              
              {/* Status indicator */}
              <div className="absolute bottom-4 right-4 bg-green-500 w-4 h-4 rounded-full animate-pulse"></div>
              
              {/* Terminal-style overlay */}
              <div className="absolute top-4 left-4 bg-black/80 px-3 py-1 rounded font-mono text-xs text-green-400">
                STATUS: ONLINE
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-cyan-400 text-2xl"></i>
      </div>
    </section>
  );
}
